// Cave84 — User document management
// On first login, ensure a /users/{uid} document exists.
// The owner role is granted ONLY to the email matching VITE_OWNER_EMAIL.
// Everyone else gets 'viewer'. The Firestore security rules also enforce this
// (no-self-elevate to owner — see firebase/firestore.rules /users path).

import { doc, getDoc, setDoc, serverTimestamp, updateDoc, addDoc, collection } from 'firebase/firestore';
import type { User as FirebaseUser } from 'firebase/auth';
import type { User, UserRole } from '@cave84/shared-types';
import { db, OWNER_EMAIL } from './firebase';

export async function ensureUserDoc(authUser: FirebaseUser): Promise<User> {
  const ref = doc(db, 'users', authUser.uid);
  const snap = await getDoc(ref);

  const isOwnerEmail =
    !!OWNER_EMAIL &&
    !!authUser.email &&
    authUser.email.toLowerCase() === OWNER_EMAIL.toLowerCase();

  if (!snap.exists()) {
    const role: UserRole = isOwnerEmail ? 'owner' : 'viewer';
    const initial = {
      email: authUser.email ?? '',
      display_name: authUser.displayName ?? authUser.email?.split('@')[0] ?? 'Unknown',
      role,
      language_pref: 'en' as const,
      created_at: serverTimestamp(),
      last_login: serverTimestamp()
    };
    await setDoc(ref, initial);
    // We re-read to get the resolved server timestamps as Timestamp objects.
    const fresh = await getDoc(ref);
    return { id: authUser.uid, ...(fresh.data() as Omit<User, 'id'>) };
  }

  // Existing user: bump last_login. Do NOT touch role here — role transitions
  // happen only via Firestore admin or the Owner explicitly editing the doc.
  await updateDoc(ref, { last_login: serverTimestamp() });
  return { id: authUser.uid, ...(snap.data() as Omit<User, 'id'>) };
}

/**
 * Append one entry to the Guest sign-in audit log (Settings > Guest Log).
 * Call once per sign-in for any user whose resolved role is NOT 'owner'.
 * Never throws into the auth flow — a failed log write should not block
 * sign-in.
 */
export async function logGuestLogin(authUser: FirebaseUser): Promise<void> {
  try {
    await addDoc(collection(db, 'guest_logins'), {
      user_id: authUser.uid,
      email: authUser.email ?? '',
      display_name: authUser.displayName ?? authUser.email?.split('@')[0] ?? 'Unknown',
      logged_in_at: serverTimestamp(),
    });
  } catch (err) {
    console.error('Cave84: failed to write guest login log entry.', err);
  }
}
